import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { to: "/", label: "首页" },
  ...categories.map((cat) => ({ to: `/category/${cat.slug}`, label: cat.name })),
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background-50 border-b border-background-200"
          : "bg-background-50/95 border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center">
            <i className="ri-play-fill text-background-50 text-xl" />
          </span>
          <span className="font-heading font-black text-lg md:text-xl tracking-tight text-foreground-950 whitespace-nowrap">
            日皮视频
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive(link.to)
                  ? "text-primary-700 bg-primary-100"
                  : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="hidden md:flex items-center flex-1 max-w-xs ml-auto bg-background-100 border border-background-200 rounded-full pl-3 pr-1 h-10"
        >
          <i className="ri-search-line text-foreground-400 text-base" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影视资讯、短视频…"
            className="flex-1 bg-transparent outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
          />
          <button
            type="submit"
            className="w-8 h-8 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="搜索"
          >
            <i className="ri-arrow-right-line text-base" />
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-900 hover:bg-background-100 cursor-pointer"
          aria-label="菜单"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form
            onSubmit={submitSearch}
            className="flex items-center bg-background-100 border border-background-200 rounded-full pl-3 pr-1 h-11 mb-4"
          >
            <i className="ri-search-line text-foreground-400 text-base" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视资讯、短视频…"
              className="flex-1 bg-transparent outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-arrow-right-line text-base" />
            </button>
          </form>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                  isActive(link.to)
                    ? "text-primary-700 bg-primary-100"
                    : "text-foreground-700"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}